import React, {useMemo, useState} from 'react';
import {
  Box,
  Button,
  Flex,
  Link,
  List,
  ListItem,
  Menu,
  MenuButton,
  MenuItem,
  MenuList,
  Popover,
  PopoverArrow,
  PopoverBody,
  PopoverContent,
  PopoverTrigger,
  Table,
  TableContainer,
  Tbody,
  Td,
  Text,
  Th,
  Thead,
  Tr,
  useDisclosure,
} from '@chakra-ui/react';
import {
  createColumnHelper,
  flexRender,
  getCoreRowModel,
  useReactTable,
} from '@tanstack/react-table';
import {getFieldLabel} from '../../shared/fields/types';
import {useCustomer, useMarkets} from '../../hooks/api/metadata';
import {pluralize} from '../../lib/helpers';
import {SimpleSignal} from '../signals';
import {assertNever} from '../../shared/util';
import {ScoringSignalToLabel} from '../../shared/signals';
import {AccountFieldLabels, isAccountField} from '../../shared/fields/keyplay';
import {IbmWatsonStudio, OverflowMenuVertical} from '@carbon/icons-react';
import {getSignalsInMarket} from '../../shared/market';
import {Link as ReactRouterLink} from 'react-router-dom';
import {SaveSignalModal} from './SignalBuilder/SaveSignalModal';
import {
  SignalDefinition,
  SignalEvalFnInput,
  getSignalEvalFnInputs,
} from '../../shared/signalDefinition';
import {DeleteSignalModal} from './DeleteSignalModal';
import _ from 'lodash';

export const Signals = () => {
  const customer = useCustomer();
  const signalDefinitions = [...(customer.signalDefinitions ?? [])].sort(
    (a, b) => a.label.localeCompare(b.label)
  );
  const createSignalModal = useDisclosure();
  const editSignalModal = useDisclosure();
  const deleteSignalModal = useDisclosure();
  const [selectedSignal, setSelectedSignal] = useState<SignalDefinition | null>(
    null
  );

  const handleEditSignal = (signalDefinition: SignalDefinition) => {
    setSelectedSignal(signalDefinition);
    editSignalModal.onOpen();
  };

  const handleDeleteSignal = (signalDefinition: SignalDefinition) => {
    setSelectedSignal(signalDefinition);
    deleteSignalModal.onOpen();
  };

  return (
    <Flex direction="column" gap="4">
      <Flex justifyContent="space-between">
        <Box>
          <Text fontWeight="500">Signals</Text>
          <Text textColor="kgray.300" fontSize="sm">
            {pluralize(signalDefinitions.length, 'signal', 'signals')}
          </Text>
        </Box>
        <Button
          colorScheme="kbuttonblue"
          fontWeight="normal"
          onClick={createSignalModal.onOpen}
        >
          Create Signal
        </Button>
      </Flex>
      <CustomSignalsList
        signalDefinitions={signalDefinitions}
        onEditSignal={handleEditSignal}
        onDeleteSignal={handleDeleteSignal}
      />
      <SaveSignalModal
        isOpen={createSignalModal.isOpen}
        onClose={createSignalModal.onClose}
      />
      {selectedSignal && (
        <>
          <SaveSignalModal
            signalDefinition={selectedSignal}
            isOpen={editSignalModal.isOpen}
            onClose={editSignalModal.onClose}
          />
          <DeleteSignalModal
            signalDefinition={selectedSignal}
            isOpen={deleteSignalModal.isOpen}
            onClose={deleteSignalModal.onClose}
          />
        </>
      )}
    </Flex>
  );
};

const CustomSignalMenu = ({
  signalDefinition,
  onEditSignal,
  onDeleteSignal,
}: {
  signalDefinition: SignalDefinition;
  onEditSignal: (signalDefinition: SignalDefinition) => void;
  onDeleteSignal: (signalDefinition: SignalDefinition) => void;
}) => {
  return (
    <Menu>
      <MenuButton>
        <OverflowMenuVertical />
      </MenuButton>
      <MenuList>
        <MenuItem onClick={() => onEditSignal(signalDefinition)}>
          Edit Signal
        </MenuItem>
        <MenuItem
          onClick={() => onDeleteSignal(signalDefinition)}
          textColor="kred.500"
        >
          Delete Signal
        </MenuItem>
      </MenuList>
    </Menu>
  );
};

const columnHelper = createColumnHelper<SignalDefinition>();
const useColumns = (callbacks: {
  onEditSignal: (signalDefinition: SignalDefinition) => void;
  onDeleteSignal: (signalDefinition: SignalDefinition) => void;
}) => {
  const fieldDefinitions = useCustomer().fieldDefinitions ?? [];
  const markets = useMarkets();

  const formatSignalEvalInput = (input: SignalEvalFnInput) => {
    const {type} = input;
    switch (type) {
      case 'field':
        if (isAccountField(input.field)) {
          return `${AccountFieldLabels[input.field]} (Keyplay Field)`;
        } else {
          const field = fieldDefinitions.find((f) => f.id.equals(input.field));
          return getFieldLabel(field);
        }

      case 'signal':
        return `${ScoringSignalToLabel[input.signal]} (Keyplay Signal)`;

      default:
        assertNever(type);
    }
  };

  return [
    columnHelper.accessor('id', {
      header: 'Signal Name',
      size: 0,
      cell: (info) => {
        return (
          <SimpleSignal
            fontSize="sm"
            signal={info.getValue()}
            w="min-content"
          />
        );
      },
    }),
    columnHelper.accessor('evalFn', {
      header: 'Field Input(s)',
      size: 400,
      cell: (info) => {
        let signalInput;
        let viewMoreInputs;

        const evalFnInputs = getSignalEvalFnInputs(info.getValue());
        if (evalFnInputs.length === 1) {
          signalInput = formatSignalEvalInput(evalFnInputs[0]);
        } else {
          signalInput = `${evalFnInputs.length} inputs`;
          viewMoreInputs = (
            <Popover trigger="hover">
              <PopoverTrigger>
                <Box cursor="help">
                  <IbmWatsonStudio />
                </Box>
              </PopoverTrigger>
              <PopoverContent p="2">
                <PopoverArrow />
                <PopoverBody>
                  <List spacing="2">
                    {evalFnInputs.map((input, index) => (
                      <ListItem
                        key={index}
                        overflow="hidden"
                        textOverflow="ellipsis"
                      >
                        {formatSignalEvalInput(input)}
                      </ListItem>
                    ))}
                  </List>
                </PopoverBody>
              </PopoverContent>
            </Popover>
          );
        }

        return (
          <Flex gap="2" alignItems="center" textColor="kgray.300">
            {signalInput}
            {viewMoreInputs}
          </Flex>
        );
      },
    }),
    columnHelper.accessor((data) => data.id, {
      id: 'usedIn',
      header: 'Used In',
      cell: (info) => {
        const marketsUsedIn = markets.filter((market) =>
          getSignalsInMarket(market).includes(info.getValue())
        );
        return (
          <Flex gap="2" justifyContent="center" textColor="kgray.300" w="80%">
            {marketsUsedIn.length ? (
              <Popover trigger="hover" size="sm">
                <PopoverTrigger>
                  <Box cursor="help" textColor="kblue.300">
                    {pluralize(marketsUsedIn.length, 'market', 'markets')}
                  </Box>
                </PopoverTrigger>
                <PopoverContent p="2">
                  <PopoverArrow />
                  <PopoverBody>
                    <List spacing="2">
                      {marketsUsedIn.map(({id, label}) => (
                        <ListItem
                          key={id.toString()}
                          overflow="hidden"
                          textOverflow="ellipsis"
                        >
                          <Link
                            as={ReactRouterLink}
                            to={`/markets/${id}`}
                            textColor="kblue.200"
                            _hover={{textDecoration: 'underline'}}
                          >
                            {label}
                          </Link>
                        </ListItem>
                      ))}
                    </List>
                  </PopoverBody>
                </PopoverContent>
              </Popover>
            ) : (
              '-'
            )}
          </Flex>
        );
      },
    }),
    columnHelper.accessor('timestamp', {
      header: 'Created',
      cell: (info) => {
        return (
          <Box textColor="kgray.300">
            {info.getValue().toLocaleDateString()}
          </Box>
        );
      },
    }),
    columnHelper.accessor((data) => data, {
      id: 'actions',
      header: '',
      size: 50,
      cell: (info) => (
        <CustomSignalMenu
          signalDefinition={info.getValue()}
          onEditSignal={callbacks.onEditSignal}
          onDeleteSignal={callbacks.onDeleteSignal}
        />
      ),
    }),
  ];
};

const CustomSignalsList = ({
  signalDefinitions,
  onEditSignal,
  onDeleteSignal,
}: {
  signalDefinitions: SignalDefinition[];
  onEditSignal: (signalDefinition: SignalDefinition) => void;
  onDeleteSignal: (signalDefinition: SignalDefinition) => void;
}) => {
  const sortedSignals = useMemo(
    () => _.orderBy(signalDefinitions, ['group', 'label'], ['desc', 'asc']),
    [signalDefinitions]
  );

  const columns = useColumns({onEditSignal, onDeleteSignal});
  const table = useReactTable({
    columns,
    data: sortedSignals,
    getRowId: (row) => row.id,
    getCoreRowModel: getCoreRowModel(),
    defaultColumn: {
      minSize: 0,
      size: 150,
    },
  });

  if (!sortedSignals.length) {
    return (
      <Flex direction="column" align="center" p={8}>
        <Text textColor="kgray.300">No custom signals defined.</Text>
      </Flex>
    );
  }

  return (
    <TableContainer border="1px" borderColor="kgray.200" borderRadius="md">
      <Table variant="simple">
        <Thead bg="kgray.100">
          {table.getHeaderGroups().map((headerGroup) => (
            <Tr key={headerGroup.id}>
              {headerGroup.headers.map((header) => (
                <Th
                  key={header.id}
                  w={header.getSize() ? `${header.getSize()}px` : 'auto'}
                >
                  {flexRender(
                    header.column.columnDef.header,
                    header.getContext()
                  )}
                </Th>
              ))}
            </Tr>
          ))}
        </Thead>
        <Tbody>
          {(() => {
            let lastGroup: string | undefined = undefined;
            let groupIndex = 0;

            return table.getRowModel().rows.map((row) => {
              const group = row.original.group?.toString();
              const isNewGroup = group !== lastGroup;

              if (isNewGroup) {
                groupIndex++;
                lastGroup = group;
              }

              // Alternate background color for each group
              const bgColor = groupIndex % 2 === 1 ? 'white' : 'kgray.100';

              return (
                <Tr key={row.id} role="group" bg={bgColor}>
                  {row.getVisibleCells().map((cell) => (
                    <Td
                      key={cell.id}
                      _groupHover={{bg: 'kgray.200'}}
                      w={
                        cell.getContext().column.getSize()
                          ? `${cell.getContext().column.getSize()}px`
                          : 'auto'
                      }
                    >
                      {flexRender(
                        cell.column.columnDef.cell,
                        cell.getContext()
                      )}
                    </Td>
                  ))}
                </Tr>
              );
            });
          })()}
        </Tbody>
      </Table>
    </TableContainer>
  );
};
